import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, RefreshCw, CreditCard, X, Plus, Trash2, FileText, CheckCircle,
  Download, AlertTriangle, Clock
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { API, formatPrice, formatDate, STATUS_BADGES } from '../utils';
import { PackName } from '../../ui/PackName';

const getStatusBadge = (status) => {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>{badge.text}</span>;
};

const DepositsView = ({ reservations, stats, fetchAllData }) => {
const [refunding, setRefunding] = useState(null);
    const [refundModal, setRefundModal] = useState(null);
    const [refundAmount, setRefundAmount] = useState('');
    const [refundReason, setRefundReason] = useState('');
    
    // Invoice creation state
    const [invoiceMode, setInvoiceMode] = useState(false);
    const [invoiceItems, setInvoiceItems] = useState([{ description: '', quantity: 1, unit_price_ht: '' }]);
    const [invoiceNotes, setInvoiceNotes] = useState('');
    const [creatingInvoice, setCreatingInvoice] = useState(false);

    const openRefundModal = (reservation) => {
      const alreadyRefunded = reservation.deposit_amount_refunded || 0;
      const maxAmount = reservation.caution - alreadyRefunded;
      setRefundModal({ reservation, maxAmount, alreadyRefunded });
      setRefundAmount(maxAmount.toString());
      setRefundReason('');
      setInvoiceMode(false);
      setInvoiceItems([{ description: '', quantity: 1, unit_price_ht: '' }]);
      setInvoiceNotes('');
    };

    const closeRefundModal = () => {
      setRefundModal(null);
      setRefundAmount('');
      setRefundReason('');
      setInvoiceMode(false);
      setInvoiceItems([{ description: '', quantity: 1, unit_price_ht: '' }]);
      setInvoiceNotes('');
    };

    // Switch to invoice mode when partial refund is needed
    const switchToInvoiceMode = () => {
      const amount = parseFloat(refundAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.error('Veuillez entrer un montant valide');
        return;
      }
      if (amount >= refundModal.maxAmount) {
        // Full refund - proceed directly
        handleRefundDeposit();
        return;
      }
      // Partial refund - switch to invoice mode
      const deductionAmount = refundModal.maxAmount - amount;
      setInvoiceItems([{ 
        description: 'Retenue sur caution', 
        quantity: 1, 
        unit_price_ht: (deductionAmount / 1.20).toFixed(2) // Convert TTC to HT
      }]);
      setInvoiceMode(true);
    };

    // Add invoice line item
    const addInvoiceItem = () => {
      setInvoiceItems([...invoiceItems, { description: '', quantity: 1, unit_price_ht: '' }]);
    };

    // Remove invoice line item
    const removeInvoiceItem = (index) => {
      if (invoiceItems.length > 1) {
        setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
      }
    };

    // Update invoice line item
    const updateInvoiceItem = (index, field, value) => {
      const updated = [...invoiceItems];
      updated[index][field] = value;
      setInvoiceItems(updated);
    };

    // Calculate invoice totals
    const calculateInvoiceTotals = () => {
      const totalHT = invoiceItems.reduce((sum, item) => {
        const qty = parseInt(item.quantity) || 0;
        const price = parseFloat(item.unit_price_ht) || 0;
        return sum + (qty * price);
      }, 0);
      const tva = totalHT * 0.20;
      const totalTTC = totalHT + tva;
      return { totalHT, tva, totalTTC };
    };

    // Handle full refund (without deduction)
    const handleRefundDeposit = async () => {
      if (!refundModal) return;
      
      const amount = parseFloat(refundAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.error('Veuillez entrer un montant valide');
        return;
      }
      if (amount > refundModal.maxAmount) {
        toast.error(`Le montant ne peut pas dépasser ${refundModal.maxAmount}€`);
        return;
      }

      const reservationId = refundModal.reservation.id;
      setRefunding(reservationId);
      closeRefundModal();
      
      try {
        const response = await axios.post(
          `${API}/admin/reservations/${reservationId}/refund-deposit`,
          { amount, reason: refundReason || null },
          { withCredentials: true }
        );
        toast.success(response.data.message || 'Remboursement effectué avec succès');
        fetchAllData();
      } catch (error) {
        console.error('Refund error:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors du remboursement');
      } finally {
        setRefunding(null);
      }
    };

    // Handle invoice creation and partial refund
    const handleCreateInvoiceAndRefund = async () => {
      if (!refundModal) return;
      
      // Validate invoice items
      const validItems = invoiceItems.filter(item => 
        item.description.trim() && 
        parseFloat(item.unit_price_ht) > 0
      );
      
      if (validItems.length === 0) {
        toast.error('Veuillez ajouter au moins un article valide');
        return;
      }

      const { totalTTC } = calculateInvoiceTotals();
      if (totalTTC > refundModal.maxAmount) {
        toast.error(`Le total TTC (${totalTTC.toFixed(2)}€) dépasse la caution restante (${refundModal.maxAmount}€)`);
        return;
      }

      setCreatingInvoice(true);
      const reservationId = refundModal.reservation.id;
      
      try {
        const response = await axios.post(
          `${API}/admin/reservations/${reservationId}/deposit-deduction-invoice`,
          {
            line_items: validItems.map(item => ({
              description: item.description,
              quantity: parseInt(item.quantity) || 1,
              unit_price_ht: parseFloat(item.unit_price_ht)
            })),
            notes: invoiceNotes || null,
            refund_remaining: true
          },
          { withCredentials: true }
        );
        
        toast.success(`Facture ${response.data.invoice.invoice_number} créée et envoyée au client`);
        if (response.data.refund) {
          toast.success(`Remboursement de ${response.data.summary.montant_rembourse.toFixed(2)}€ effectué`);
        }
        closeRefundModal();
        fetchAllData();
      } catch (error) {
        console.error('Invoice creation error:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de la création de la facture');
      } finally {
        setCreatingInvoice(false);
      }
    };

    const isRestocked = (res) => ['returned', 'received'].includes(res.status);

    const getDepositStatusBadge = (res) => {
      const isFullyRefunded = res.deposit_status === 'refunded';
      const isPartiallyRefunded = res.deposit_status === 'partially_refunded';
      const alreadyRefunded = res.deposit_amount_refunded || 0;
      const remaining = res.caution - alreadyRefunded;

      if (isFullyRefunded) {
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
            Remboursée ({res.caution}€)
          </span>
        );
      }
      if (isPartiallyRefunded) {
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400">
            Partiel ({alreadyRefunded}€ / {res.caution}€)
          </span>
        );
      }
      if (!isRestocked(res)) {
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            Retenue
          </span>
        );
      }
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
          À restituer
        </span>
      );
    };

    const canRefund = (res) => {
      const isFullyRefunded = res.deposit_status === 'refunded';
      // Caution restituable uniquement APRÈS réception/remise en stock du matériel.
      return isRestocked(res) && !isFullyRefunded;
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Gestion des cautions</h2>
          <div className="text-right">
            <p className="text-sm text-slate-400">Total cautions en cours</p>
            <p className="text-2xl font-bold text-[#DFFF00]">{formatPrice(stats?.total_deposits || 0)}</p>
          </div>
        </div>

        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Client</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Pack</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Caution</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Remboursé</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Date</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {reservations.filter(r => r.caution > 0).map((res) => {
                const isFullyRefunded = res.deposit_status === 'refunded';
                const isRefunding = refunding === res.id;
                const alreadyRefunded = res.deposit_amount_refunded || 0;
                const remaining = res.caution - alreadyRefunded;
                
                return (
                  <tr key={res.id} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3">
                      <p className="text-sm text-white">{res.customer?.firstName} {res.customer?.lastName}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300"><PackName name={res.pack_name} /></td>
                    <td className="px-4 py-3 text-sm text-white font-medium">{formatPrice(res.caution)}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">
                      {alreadyRefunded > 0 ? (
                        <span className="text-blue-400">{formatPrice(alreadyRefunded)}</span>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">{formatDate(res.created_at)}</td>
                    <td className="px-4 py-3">
                      {getDepositStatusBadge(res)}
                    </td>
                    <td className="px-4 py-3">
                      {isFullyRefunded ? (
                        <span className="text-xs text-blue-400 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Terminé
                        </span>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className={`text-xs ${canRefund(res) 
                            ? 'border-green-500 text-green-400 hover:bg-green-500/10' 
                            : 'border-slate-700 text-slate-500 cursor-not-allowed'
                          }`}
                          onClick={() => canRefund(res) && openRefundModal(res)}
                          disabled={!canRefund(res) || isRefunding}
                          title={!canRefund(res) && !isRestocked(res) ? "Disponible après réception et remise en stock du matériel" : undefined}
                        >
                          {isRefunding ? (
                            <>
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                              Remboursement...
                            </>
                          ) : (
                            `Restituer${remaining < res.caution ? ` (${remaining}€ restant)` : ''}`
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Refund Modal with Invoice Mode */}
        {refundModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-8">
            <div className={`bg-slate-900 rounded-2xl border border-slate-700 p-6 mx-4 ${invoiceMode ? 'w-full max-w-3xl' : 'w-full max-w-md'}`}>
              
              {!invoiceMode ? (
                // Step 1: Choose refund amount
                <>
                  <h3 className="text-xl font-bold text-white mb-4">Restitution de caution</h3>
                  
                  <div className="space-y-4">
                    {/* Client Info */}
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-sm text-slate-400">Client</p>
                      <p className="text-white font-medium">
                        {refundModal.reservation.customer?.firstName} {refundModal.reservation.customer?.lastName}
                      </p>
                      <p className="text-sm text-slate-400 mt-2">Pack: <PackName name={refundModal.reservation.pack_name} /></p>
                    </div>

                    {/* Amount Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400">Caution totale</p>
                        <p className="text-lg font-bold text-white">{formatPrice(refundModal.reservation.caution)}</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400">Déjà remboursé</p>
                        <p className="text-lg font-bold text-blue-400">{formatPrice(refundModal.alreadyRefunded)}</p>
                      </div>
                    </div>

                    {/* Refund Amount Input */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Montant à rembourser (max: {refundModal.maxAmount}€)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          max={refundModal.maxAmount}
                          min={0}
                          step="0.01"
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#DFFF00]"
                          placeholder="0.00"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                      </div>
                      {/* Quick amount buttons */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <button
                          onClick={() => setRefundAmount(refundModal.maxAmount.toString())}
                          className="px-3 py-1 text-xs bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition border border-green-500/30"
                        >
                          Tout ({refundModal.maxAmount}€)
                        </button>
                        {refundModal.maxAmount >= 100 && (
                          <button
                            onClick={() => setRefundAmount((refundModal.maxAmount - 100).toString())}
                            className="px-3 py-1 text-xs bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg transition border border-orange-500/30"
                          >
                            -{formatPrice(100)} retenue
                          </button>
                        )}
                        {refundModal.maxAmount >= 200 && (
                          <button
                            onClick={() => setRefundAmount((refundModal.maxAmount - 200).toString())}
                            className="px-3 py-1 text-xs bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg transition border border-orange-500/30"
                          >
                            -{formatPrice(200)} retenue
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Notice for partial refund */}
                    {refundAmount && parseFloat(refundAmount) < refundModal.maxAmount && parseFloat(refundAmount) > 0 && (
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                        <p className="text-sm text-orange-400">
                          <strong>Retenue de {(refundModal.maxAmount - parseFloat(refundAmount)).toFixed(2)}€</strong><br/>
                          Une facture de retenue sera générée et envoyée au client.
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={closeRefundModal}
                        className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={switchToInvoiceMode}
                        className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold"
                        disabled={!refundAmount || parseFloat(refundAmount) <= 0}
                      >
                        {parseFloat(refundAmount) >= refundModal.maxAmount 
                          ? `Rembourser ${formatPrice(parseFloat(refundAmount))}` 
                          : 'Créer la facture de retenue →'}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                // Step 2: Invoice creation for partial refund
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white">Facture de retenue sur caution</h3>
                      <p className="text-sm text-slate-400">
                        Client: {refundModal.reservation.customer?.firstName} {refundModal.reservation.customer?.lastName}
                      </p>
                    </div>
                    <button 
                      onClick={() => setInvoiceMode(false)}
                      className="text-slate-400 hover:text-white"
                    >
                      ← Retour
                    </button>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-400">Caution</p>
                      <p className="text-lg font-bold text-white">{formatPrice(refundModal.maxAmount)}</p>
                    </div>
                    <div className="bg-orange-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-orange-400">Retenue TTC</p>
                      <p className="text-lg font-bold text-orange-400">{formatPrice(calculateInvoiceTotals().totalTTC)}</p>
                    </div>
                    <div className="bg-green-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-green-400">À rembourser</p>
                      <p className="text-lg font-bold text-green-400">
                        {formatPrice(Math.max(0, refundModal.maxAmount - calculateInvoiceTotals().totalTTC))}
                      </p>
                    </div>
                  </div>

                  {/* Invoice Items */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-300">Articles de la facture</label>
                      <button
                        onClick={addInvoiceItem}
                        className="text-xs bg-slate-800 hover:bg-slate-700 text-[#DFFF00] px-3 py-1 rounded-lg transition"
                      >
                        + Ajouter une ligne
                      </button>
                    </div>
                    
                    {invoiceItems.map((item, index) => (
                      <div key={index} className="bg-slate-800/50 rounded-lg p-3">
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-6">
                            <label className="text-xs text-slate-400 block mb-1">Description</label>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#DFFF00]"
                              placeholder="Ex: Dommage dalle LED #3"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Qté</label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateInvoiceItem(index, 'quantity', e.target.value)}
                              min={1}
                              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#DFFF00]"
                            />
                          </div>
                          <div className="col-span-3">
                            <label className="text-xs text-slate-400 block mb-1">Prix HT (€)</label>
                            <input
                              type="number"
                              value={item.unit_price_ht}
                              onChange={(e) => updateInvoiceItem(index, 'unit_price_ht', e.target.value)}
                              min={0}
                              step="0.01"
                              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#DFFF00]"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="col-span-1">
                            {invoiceItems.length > 1 && (
                              <button
                                onClick={() => removeInvoiceItem(index)}
                                className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded px-2 py-2 transition"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Invoice Totals */}
                  <div className="bg-slate-800 rounded-lg p-4 mb-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-slate-400">
                        <span>Total HT</span>
                        <span>{calculateInvoiceTotals().totalHT.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-slate-400">
                        <span>TVA (20%)</span>
                        <span>{calculateInvoiceTotals().tva.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-slate-700">
                        <span>Total TTC</span>
                        <span className="text-orange-400">{calculateInvoiceTotals().totalTTC.toFixed(2)} €</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-slate-300 block mb-2">Notes (optionnel)</label>
                    <textarea
                      value={invoiceNotes}
                      onChange={(e) => setInvoiceNotes(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#DFFF00] h-20"
                      placeholder="Notes supplémentaires à inclure sur la facture..."
                    />
                  </div>

                  {/* Validation error */}
                  {calculateInvoiceTotals().totalTTC > refundModal.maxAmount && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-400">
                        Le total TTC ({calculateInvoiceTotals().totalTTC.toFixed(2)}€) dépasse la caution restante ({refundModal.maxAmount}€)
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={closeRefundModal}
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={handleCreateInvoiceAndRefund}
                      className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold"
                      disabled={
                        creatingInvoice || 
                        calculateInvoiceTotals().totalTTC <= 0 ||
                        calculateInvoiceTotals().totalTTC > refundModal.maxAmount ||
                        !invoiceItems.some(i => i.description.trim() && parseFloat(i.unit_price_ht) > 0)
                      }
                    >
                      {creatingInvoice ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Création en cours...
                        </>
                      ) : (
                        <>
                          Créer facture et rembourser {formatPrice(Math.max(0, refundModal.maxAmount - calculateInvoiceTotals().totalTTC))}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

export default DepositsView;
